import { useState, useRef, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  useLazyGetCertificateListDataQuery,
  useLazyGetEmployeeListDataQuery,
} from "../../../../../store/queries/PhotoSection";
import { submitPhotoUploadWork } from "../../api";
import {
  getPhotoSectionList,
  updateConfig,
} from "../../../../../store/slices/PhotoSection/PhotoSectionSlice";
import { useDispatch } from "react-redux";

const usePhotoUploadWorkModal = ({ handleClose, refetch, state }) => {
  const [selectAll, setSelectAll] = useState(false);
  const dispatch = useDispatch();
  const [triggerGetCertificates] = useLazyGetCertificateListDataQuery();
  const [triggerGetEmployees] = useLazyGetEmployeeListDataQuery();
  const receiptIdRef = useRef(state?.selectedId);
  receiptIdRef.current = state?.selectedId;
  const slug = sessionStorage?.getItem("slug");

  const loadEmployeeOptions = useCallback(
    async (search, loadedOptions, { page }) => {
      try {
        const result = await triggerGetEmployees({
          search,
          page: page || 1,
          page_size: 20,
        }).unwrap();

        const empData = result?.data?.data || result?.data || [];
        const options = empData.map((item) => ({
          label: item.name,
          value: item._id ?? item.id,
        }));

        return {
          options,
          hasMore: empData.length === 20,
          additional: {
            page: (page || 1) + 1,
          },
        };
      } catch (error) {
        return {
          options: [],
          hasMore: false,
          additional: { page: page || 1 },
        };
      }
    },
    [triggerGetEmployees],
  );

  const loadCertificateOptions = useCallback(
    async (search, loadedOptions, { page }) => {
      try {
        const result = await triggerGetCertificates({
          receipt_id: receiptIdRef.current,
          search,
          page: page || 1,
          page_size: 20,
        }).unwrap();

        const certData = result?.data?.data || result?.data || [];
        const options = certData.map((item) => ({
          label: item.certificate_number,
          value: item.id ?? item._id,
        }));

        return {
          options,
          hasMore: certData.length === 20,
          additional: {
            page: (page || 1) + 1,
          },
        };
      } catch (error) {
        return {
          options: [],
          hasMore: false,
          additional: { page: page || 1 },
        };
      }
    },
    [triggerGetCertificates],
  );

  const selectAllRef = useRef(false);

  const formatTimeToAmPm = (time) => {
    if (!time) return "";
    const [hourStr, minute] = time.split(":");
    const hour = parseInt(hourStr, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:${minute} ${period}`;
  };

  const initialValues = {
    start_time: "",
    end_time: "",
    certificate: [],
    employee: null,
    remarks: "",
  };

  const validation = Yup.object({
    start_time: Yup.string().required("Start time is required"),
    end_time: Yup.string().required("End time is required"),
    certificate: Yup.mixed().test(
      "certificate-required",
      "Certificate is required",
      function () {
        return (
          selectAllRef.current ||
          (this.parent.certificate && this.parent.certificate.length > 0)
        );
      },
    ),
    employee: Yup.object().nullable().required("Employee is required"),
    remarks: Yup.string(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: validation,
    onSubmit: async (values) => {
      const certificateIds = selectAll
        ? []
        : values.certificate.map((c) => c.label);
      const params = {
        start_time: formatTimeToAmPm(values.start_time),
        end_time: formatTimeToAmPm(values.end_time),
        certificates: certificateIds,
        select_all: selectAll,
        employee_id: values.employee?.value,
        remark: values.remarks,
        receipt_id: receiptIdRef.current,
      };
      try {
        const response = await submitPhotoUploadWork(params);
        if (response?.data?.status_code === 200) {
          toast.success(
            response?.data?.message ??
              "Photo upload work submitted successfully",
          );
          dispatch(
            updateConfig((state) => {
              state.rawPhotoWorkCompleted =
                response?.data?.data?.raw_photo_completed;
            }),
          );
          dispatch(
            getPhotoSectionList({
              page: 1,
              per_page: 10,
              process_slug: slug,
            }),
          );
          dispatch(
            updateConfig((state) => {
              state.cardData = {
                Pending: [],
              };
            }),
          );
          handleClose();
          refetch?.();
        } else {
          toast.error(response?.data?.message ?? "Something went wrong");
        }
      } catch (error) {
        toast.error("API request failed");
      }
    },
  });

  const handleSelectAll = useCallback(
    (checked) => {
      setSelectAll(checked);
      selectAllRef.current = checked;
      if (checked) {
        // Clear individual selections — API uses select_all flag
        formik.setFieldValue("certificate", []);
      }
    },
    [formik],
  );

  const handleCertificateChange = useCallback(
    (selectedOptions) => {
      const selected = selectedOptions || [];
      formik.setFieldValue("certificate", selected);
    },
    [formik],
  );

  return {
    formik,
    loadEmployeeOptions,
    loadCertificateOptions,
    selectAll,
    handleSelectAll,
    handleCertificateChange,
  };
};

export default usePhotoUploadWorkModal;
